import { Component, computed, effect, ElementRef, HostListener, inject, input, signal, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { GameService } from '@core/game/game.service';
import { LpActionsService } from '@features/lp/lp-actions/lp-actions.service';
import { LpConditionsService } from '@features/lp/lp-conditions/lp-conditions.service';
import { LpMissionService } from '@features/lp/lp-mission/lp-mission.service';
import { LpVariablesService } from '@features/lp/lp-variables/lp-variables.service';
import { LpIFrameActivity } from '@typings/lp/generated';

@Component({
  selector: 'lp-iframe-activity',
  imports: [],
  templateUrl: './lp-iframe-activity.component.html',
  styleUrl: './lp-iframe-activity.component.css',
})
export class LpIframeActivityComponent {
  readonly #domSanitizer = inject(DomSanitizer);
  readonly #gameService = inject(GameService);
  readonly #lpActionsService = inject(LpActionsService);
  readonly #lpConditionsService = inject(LpConditionsService);
  readonly #lpMissionService = inject(LpMissionService);
  readonly #lpVariablesService = inject(LpVariablesService);

  activity = input.required<LpIFrameActivity>();

  url = computed(() => this.#domSanitizer.bypassSecurityTrustResourceUrl(this.activity().iFrameUrl));
  origin = computed(() => new URL(this.activity().iFrameUrl).origin);
  isReady = signal(false);

  @ViewChild('frame', { static: true }) frame!: ElementRef<HTMLIFrameElement>;

  constructor() {
    effect(() => {
      this.#gameService.subscribe();

      this.frame.nativeElement.contentWindow?.postMessage(
        {
          type: 'state',
          data: {
            variables: this.#lpVariablesService
              .stateList()
              .map(({ variable, value }) => ({ id: variable.variableRef, value: value ?? null }))
              .reduce((acc, { id, value }) => ({ ...acc, [id]: value }), {} as Record<string, string | null>),
            inventory: this.#gameService.inventoryUids(),
            finishedMissions: this.#lpMissionService.finishedMissions().map((mission) => mission.uid),
            mapId: this.#gameService.mapUid() ?? null,
            missionId: this.#gameService.missionUid() ?? null,
          },
        },
        this.origin(),
      );
    });
  }

  @HostListener('window:message', ['$event'])
  async onMessage(event: MessageEvent) {
    if (event.origin !== this.origin()) {
      return;
    }

    if (event.data.type === 'evaluateCondition') {
      const { key, callId } = event.data.data as { key: string; callId: string };
      const conditionMapping = this.activity().mappingConditions.find((c) => c.key === key);
      const condition = conditionMapping?.condition;
      if (!condition) {
        throw new Error(`Condition with key '${key}' not found in activity mapping conditions.`);
      }

      const result = await this.#lpConditionsService.evaluate(condition);
      this.frame.nativeElement.contentWindow?.postMessage(
        {
          type: 'evaluatedCondition',
          data: {
            callId,
            result,
          },
        },
        this.origin(),
      );
      return;
    }

    if (event.data.type === 'performEvent') {
      const { key } = event.data.data as { key: string };
      const eventMapping = this.activity().mappingEvents.find((e) => e.key === key);
      const _event = eventMapping?.event;
      if (!_event) {
        throw new Error(`Event with key '${key}' not found in activity mapping events.`);
      }
      await this.#lpActionsService.perform(_event);
      return;
    }

    if (event.data.type === 'ready') {
      this.isReady.set(true);
      this.frame.nativeElement.contentWindow?.postMessage(
        {
          type: 'setup',
          data: {
            userId: '// TODO',
            properties: this.activity().initializationData ?? '',
            mappings: {
              conditions: this.activity().mappingConditions.reduce(
                (acc, condition) => ({ ...acc, [condition.key]: condition.uid }),
                {} as Record<string, string>,
              ),
              events: this.activity().mappingEvents.reduce(
                (acc, event) => ({ ...acc, [event.key]: event.uid }),
                {} as Record<string, string>,
              ),
              variables: this.activity().mappingVariables.reduce(
                (acc, variable) => ({
                  ...acc,
                  [variable.key]: variable.variable.variableRef,
                }),
                {} as Record<string, string>,
              ),
            },
          },
        },
        this.origin(),
      );
      return;
    }

    if (event.data.type === 'setVariable') {
      const { key, value } = event.data.data as { key: string; value: any };
      const variableMapping = this.activity().mappingVariables.find((v) => v.key === key);
      const variable = variableMapping?.variable;
      if (!variable) {
        throw new Error(`Variable with key '${key}' not found in activity mapping variables.`);
      }

      this.#lpVariablesService.set(variable, value);
      return;
    }

    if (event.data.type === 'finish') {
      await this.#lpMissionService.finishActivity(this.activity());
      return;
    }

    console.log('Message received from iframe:', event.data);
  }
}
